import isTrustworthy from './isTrustworthy';
import getEnv from '../environment/resolve';

/**
 * Which myf2b host to send the user back to.
 *
 * `getEnv().appHost` is resolved from the DASHBOARD's own hostname, so
 * dashboard.stage.myf2b.com always answers `https://stage.myf2b.com`. That is
 * correct for stage and prod and wrong for a release environment: arrive from
 * `v101.stage.myf2b.com`, click the logo or any menu item, and you are silently
 * back on stage — no longer testing the release branch you opened.
 *
 * myf2b writes the host it is actually being browsed on to a `myf2b_origin`
 * cookie scoped to the shared parent domain (ApplicationController
 * #set_app_origin_cookie), which is readable here because dashboard.<domain>
 * sits under it — the same property the shared session already relies on.
 *
 * The cookie is untrusted input, so it goes through the same `isTrustworthy`
 * check already applied to `return_url` on the login page: any host outside
 * myf2b.com / myf2b.net / footsteps2brilliance.com is ignored and we fall back
 * to the configured appHost. A missing or stale cookie degrades to exactly
 * today's behaviour, which is why this is safe to ship before the myf2b side.
 */
export const readOriginCookie = () => {
	if(typeof document === 'undefined'){ return null; }
	const match = document.cookie.match(/(?:^|;\s*)myf2b_origin=([^;]*)/);
	if(!match){ return null; }
	// Rails percent-encodes cookie values, and this one contains "://".
	const value = decodeURIComponent(match[1]);
	return isTrustworthy(value) ? value : null;
};

/**
 * The four permanent app hosts. Used twice below, for opposite reasons.
 */
const APP_HOSTS = ['myf2b.com', 'www.myf2b.com', 'stage.myf2b.com', 'dev.myf2b.com'];

/**
 * The app origin to link to: the host being browsed when it is one this
 * environment's appHost cannot already account for, otherwise appHost itself.
 *
 * Two conditions, and both exist to keep the cookie from changing anything on
 * the permanent environments:
 *
 * 1. Not one of the four permanent hosts. `production.json` sets appHost to
 *    `https://myf2b.com`, so without this a visitor arriving on
 *    `www.myf2b.com` would have every menu link quietly repointed at www — a
 *    behaviour change on production, in exchange for nothing, since both hosts
 *    already serve the app. When the cookie names a host we know about, appHost
 *    is already the right answer.
 *
 * 2. A strict subdomain of appHost. Release environments live *under* the host
 *    they clone (`v101.stage.myf2b.com`), which is exactly the relationship
 *    that makes the shared session cookie work in the first place. Anything
 *    else — a myf2b.net host, or dev's cookie surviving into a stage tab — is
 *    not this environment's to follow.
 */
const isStrictSubdomainOf = (host, base) => host !== base && host.endsWith(`.${base}`);

export const appOrigin = () => {
	const configured = getEnv().appHost;
	const browsing = readOriginCookie();
	if(!browsing){ return configured; }

	try {
		const browsingHost = new URL(browsing).host;
		const configuredHost = new URL(configured).host;
		if(APP_HOSTS.includes(browsingHost)){ return configured; }
		if(!isStrictSubdomainOf(browsingHost, configuredHost)){ return configured; }
		return browsing;
	}
	catch(e) {
		console.warn(e);
		return configured;
	}
};

/**
 * Repoint a myf2b URL at `appOrigin()`.
 *
 * Allowlisted hosts only, deliberately, and NOT a `.myf2b.com` suffix match.
 * Menu items with `type: 'External Link'` carry whatever href the section was
 * configured with — a district's own site, a Google Form — and those must
 * survive untouched. The same trap is documented on withAppOrigin for CMS
 * assets served from f2b-cms.s3.amazonaws.com.
 *
 * Menu URLs are built by myf2b from the request host of the GraphQL Lambda, so
 * in practice the host to rewrite is always the environment's own appHost;
 * matching the wider allowlist costs nothing and survives that changing.
 */

export const withBrowsingOrigin = (url) => {
	if(!url){ return url; }
	try {
		const target = appOrigin();
		const Url = new URL(url, target);
		if(!APP_HOSTS.includes(Url.host)){ return url; }
		const origin = new URL(target);
		Url.protocol = origin.protocol;
		Url.host = origin.host;
		return Url.href;
	}
	catch(e) {
		// A malformed href is the CMS's problem, not ours — hand it back
		// unchanged rather than blanking a menu item.
		console.warn(e);
		return url;
	}
};

export default appOrigin;
